import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Flag, ArrowRight, Compass } from "lucide-react";

/* -- adventure-themed copy ---------------------------------------------- */
const FIRST_WRONG_MESSAGES = [
  "Not quite — give it one more try! You've got this! 🧭",
  "Close, but the trail bends differently here. Try again, Explorer! 🏔️",
  "Almost — adjust your compass and pick again.",
];
const SECOND_RIGHT_MESSAGES = (ap) => `You got it on the second try — great persistence Explorer! +${ap} AP 🎉`;
const FINAL_WRONG_MESSAGE = "No worries — every explorer misses a trail marker sometimes. Review this section and you will nail it next time! 🏔️";

const pickRand = (arr) => arr[Math.floor(Math.random() * arr.length)];

/* -- helpers ------------------------------------------------------------ */
// Each question is its own little state machine: "first" → "second_wrong" →
// "correct" | "final_wrong"
const QUESTION_STATE = { FIRST: "first", SECOND_WRONG: "second_wrong", CORRECT: "correct", FINAL_WRONG: "final_wrong" };

const QuizFlow = ({ expeditionId, stage, originalIndex, onExit, onComplete }) => {
  // Per-question internal state, keyed by question index in the shuffled order
  const quiz = stage.quiz || [];
  const total = quiz.length;
  const apPerQuestionMax = total > 0 ? Math.round(50 / total) : 0;

  // Per-q tracked attempts: { [qi]: { state, firstWrong (option text), secondWrong (option text), firstCorrect, secondCorrect, apEarned, msg } }
  const [perQ, setPerQ] = useState(() =>
    Object.fromEntries(quiz.map((_, qi) => [qi, {
      state: QUESTION_STATE.FIRST,
      firstAttemptCorrect: null, secondAttemptCorrect: null,
      firstAttemptValue: null, secondAttemptValue: null,
      firstDisabledIdx: null,  // option index to lock out on 2nd attempt
      apEarned: 0, msg: null,
    }]))
  );

  // The submit-to-server step happens at the end; while in progress we just
  // track per-question state locally.
  const [submitting, setSubmitting] = useState(false);
  const [serverResult, setServerResult] = useState(null);
  // Rescue-route choice state — when the server returns offer_rescue_route=true
  // and the climber's school score < 70%, we present a 2-button choice that
  // must be answered before they can leave the summary screen.
  const [rescueChoice, setRescueChoice] = useState(null);   // null | "accepted" | "declined"
  const [rescueBusy, setRescueBusy] = useState(false);

  // Has the climber answered every question (either correct or exhausted both attempts)?
  const allAnswered = useMemo(
    () => quiz.every((_, qi) => {
      const s = perQ[qi]?.state;
      return s === QUESTION_STATE.CORRECT || s === QUESTION_STATE.FINAL_WRONG;
    }),
    [perQ, quiz]
  );

  const answeredCount = quiz.filter((_, qi) => {
    const s = perQ[qi]?.state;
    return s === QUESTION_STATE.CORRECT || s === QUESTION_STATE.FINAL_WRONG;
  }).length;

  /* Each radio click handles all four state transitions. */
  const selectOption = (qi, oi) => {
    setPerQ((prev) => {
      const cur = prev[qi];
      if (!cur) return prev;
      if (cur.state === QUESTION_STATE.CORRECT || cur.state === QUESTION_STATE.FINAL_WRONG) return prev;
      const q = quiz[qi];
      const correct = oi === q.answer;
      const value = q.options?.[oi];
      if (cur.state === QUESTION_STATE.FIRST) {
        if (correct) {
          return { ...prev, [qi]: { ...cur, state: QUESTION_STATE.CORRECT,
            firstAttemptCorrect: true, firstAttemptValue: value,
            apEarned: apPerQuestionMax, msg: `Spot on! +${apPerQuestionMax} AP` } };
        }
        return { ...prev, [qi]: { ...cur, state: QUESTION_STATE.SECOND_WRONG,
          firstAttemptCorrect: false, firstAttemptValue: value, firstDisabledIdx: oi,
          msg: pickRand(FIRST_WRONG_MESSAGES) } };
      }
      // SECOND_WRONG → either CORRECT (2nd try right) or FINAL_WRONG
      if (cur.state === QUESTION_STATE.SECOND_WRONG) {
        if (correct) {
          return { ...prev, [qi]: { ...cur, state: QUESTION_STATE.CORRECT,
            secondAttemptCorrect: true, secondAttemptValue: value,
            apEarned: apPerQuestionMax, msg: SECOND_RIGHT_MESSAGES(apPerQuestionMax) } };
        }
        return { ...prev, [qi]: { ...cur, state: QUESTION_STATE.FINAL_WRONG,
          secondAttemptCorrect: false, secondAttemptValue: value,
          msg: FINAL_WRONG_MESSAGE } };
      }
      return prev;
    });
  };

  const submitQuiz = async () => {
    setSubmitting(true);
    const attempts = quiz.map((_, qi) => {
      const orig = typeof originalIndex === "function" ? originalIndex(qi) : qi;
      const c = perQ[qi] || {};
      return {
        question_index: orig,
        first_attempt_correct: c.firstAttemptCorrect,
        second_attempt_correct: c.secondAttemptCorrect,
        first_attempt_value: c.firstAttemptValue,
        second_attempt_value: c.secondAttemptValue,
      };
    });
    try {
      const r = await api.post(`/expeditions/${expeditionId}/complete-stage`, {
        stage_name: stage.name,
        attempts,
      });
      setServerResult(r.data);
      const ap = r.data.ap_earned || 0;
      toast.success(`Stage complete! +${ap} AP earned`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit quiz");
    } finally {
      setSubmitting(false);
    }
  };

  /* ===== Summary screen ===== */
  if (serverResult) {
    const totalQ = serverResult.total_questions ?? total;
    const correct = serverResult.correct_count ?? 0;
    const percent = Math.round(serverResult.score_percent ?? 0);
    const offerRescue = !!serverResult.offer_rescue_route;
    const autoRescue = !!serverResult.rescue_mode_triggered;
    // The climber must answer the rescue choice before continuing
    const continueBlocked = offerRescue && rescueChoice === null;

    const acceptRescue = async () => {
      if (!serverResult.student_id) return;
      setRescueBusy(true);
      try {
        await api.post(`/students/${serverResult.student_id}/set-rescue-mode`, { mode: true });
        setRescueChoice("accepted");
        toast.success("Rescue Route is on — your next expedition will be extra-supportive.");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not switch to Rescue Route");
      } finally {
        setRescueBusy(false);
      }
    };
    const declineRescue = () => {
      setRescueChoice("declined");
      toast.success("Onward, Explorer! Keep climbing the standard trail.");
    };

    return (
      <div data-testid="quiz-summary">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel flex items-center gap-1.5">
          <Flag size={12} strokeWidth={2.5} /> Trail check complete
        </div>
        <h2 className="font-heading text-2xl font-black text-mm-navy mt-1">Summit summary — {stage.title}</h2>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryStat label="Score" value={`${percent}%`} accent />
          <SummaryStat label="Correct" value={`${correct}/${totalQ}`} />
          <SummaryStat label="AP earned" value={`+${serverResult.ap_earned ?? 0}`} accent />
          <SummaryStat label="Streak" value={`${serverResult.streak ?? 0}d`} />
        </div>

        <div className="mt-5 rounded-xl bg-mm-parchment border border-mm-navy/10 p-4 text-mm-navy" data-testid="quiz-summary-feedback">
          <div className="text-xs uppercase tracking-widest font-bold text-mm-steel mb-1">Trail leader says</div>
          <div className="text-mm-navy/90">{serverResult.feedback_message}</div>
        </div>

        {autoRescue && (
          <div className="mt-4 rounded-xl bg-mm-gold/15 border-2 border-mm-gold/40 p-4" data-testid="quiz-summary-rescue">
            <div className="font-heading font-black text-mm-navy flex items-center gap-2">
              <Compass size={16} strokeWidth={2.5} /> Personalized path unlocked
            </div>
            <p className="text-sm text-mm-navy/85 mt-1">
              We are going to take a slightly different path to make sure you are fully prepared for the Summit. Your Rescue Route is ready! 🧭
            </p>
          </div>
        )}

        {offerRescue && (
          <div
            className={`mt-4 rounded-xl border-2 p-5 ${
              rescueChoice === "accepted"
                ? "bg-mm-gold/15 border-mm-gold/40"
                : rescueChoice === "declined"
                ? "bg-mm-steel/10 border-mm-steel/30"
                : "bg-amber-50 border-amber-300"
            }`}
            data-testid="rescue-route-choice"
          >
            <div className="font-heading font-black text-mm-navy flex items-center gap-2">
              <Compass size={16} strokeWidth={2.5} />
              {rescueChoice === "accepted"
                ? "Rescue Route activated"
                : rescueChoice === "declined"
                ? "Continuing on the standard trail"
                : "Which trail next?"}
            </div>
            {rescueChoice === null && (
              <>
                <p className="text-sm text-mm-navy/85 mt-1">
                  That stretch was tough! You can keep climbing the standard trail, OR switch to the Rescue Route — slower pace, smaller steps, friendlier language — to make sure nothing falls through the cracks.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    onClick={declineRescue}
                    disabled={rescueBusy}
                    className="btn-trail btn-outline-navy text-sm disabled:opacity-50"
                    data-testid="rescue-decline-btn"
                  >
                    Keep climbing as normal
                  </button>
                  <button
                    onClick={acceptRescue}
                    disabled={rescueBusy}
                    className="btn-trail btn-gold text-sm disabled:opacity-50"
                    data-testid="rescue-accept-btn"
                  >
                    {rescueBusy ? "Switching…" : "Switch to Rescue Route"}
                  </button>
                </div>
              </>
            )}
            {rescueChoice === "accepted" && (
              <p className="text-sm text-mm-navy/85 mt-1">
                Your next expedition will be a Rescue Route with extra scaffolding. You can switch back any time from the climber settings.
              </p>
            )}
            {rescueChoice === "declined" && (
              <p className="text-sm text-mm-navy/85 mt-1">
                Standard trail it is — onward, Explorer! Review the missed questions below before your next stage.
              </p>
            )}
          </div>
        )}

        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest font-bold text-mm-steel mb-2">Per-question breakdown</div>
          <ul className="space-y-2" data-testid="quiz-summary-breakdown">
            {(serverResult.results || []).map((r, i) => (
              <li key={i} className={`rounded-lg border p-3 ${r.is_correct ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
                <div className="flex items-start gap-2">
                  {r.is_correct
                    ? <Check size={16} strokeWidth={3} className="text-emerald-700 mt-0.5 shrink-0" />
                    : <X size={16} strokeWidth={3} className="text-red-700 mt-0.5 shrink-0" />}
                  <div className="flex-1">
                    <div className="font-bold text-mm-navy">{r.question}</div>
                    {!r.is_correct && (
                      <div className="text-xs text-mm-navy/75 mt-1">
                        Correct answer: <span className="font-bold text-emerald-800">{r.options?.[r.correct_answer_index]}</span>
                      </div>
                    )}
                    <div className="text-[11px] text-mm-navy/55 mt-0.5">
                      {r.is_correct
                        ? (r.attempt_used === 1 ? "Got it first try" : "Got it second try")
                        : "Missed — both attempts incorrect"}
                      {` · +${r.ap_earned} AP`}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-7 flex flex-wrap gap-2">
          {percent < 70 && (
            <button onClick={onExit} className="btn-trail btn-outline-navy text-sm" data-testid="quiz-summary-review-btn">
              Review trail
            </button>
          )}
          <button
            onClick={() => !continueBlocked && onComplete && onComplete(serverResult)}
            disabled={continueBlocked}
            className="btn-trail btn-gold text-sm disabled:opacity-50"
            data-testid="quiz-summary-continue-btn"
          >
            Continue to next trail stage <ArrowRight size={14} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    );
  }

  /* ===== Quiz form ===== */
  return (
    <div data-testid="quiz-form">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Trail check</div>
          <h2 className="font-heading text-2xl font-black text-mm-navy mt-1">{stage.title}</h2>
        </div>
        <div className="text-xs font-bold text-mm-steel">{answeredCount}/{total}</div>
      </div>

      <div className="mt-5 space-y-5">
        {quiz.map((q, qi) => {
          const cur = perQ[qi] || {};
          const correctIdx = q.answer;
          const locked = cur.state === QUESTION_STATE.CORRECT || cur.state === QUESTION_STATE.FINAL_WRONG;
          return (
            <div key={`${stage.name}-q-${qi}-${q.question}`} className="border border-mm-navy/10 rounded-xl p-4" data-testid={`quiz-question-${qi}`}>
              <div className="font-heading font-bold text-mm-navy">{qi + 1}. {q.question}</div>

              <div className="mt-3 grid gap-2">
                {q.options?.map((opt, oi) => {
                  // Determine the visual + interactive state of this specific option.
                  const isFirstWrong = cur.firstDisabledIdx === oi && !locked && cur.state === QUESTION_STATE.SECOND_WRONG;
                  // For the locked (final) view, mark any option the climber tried as "attempted"
                  const isAttempted = (cur.firstAttemptValue === opt) || (cur.secondAttemptValue === opt);
                  let cls = "border-mm-navy/10 hover:border-mm-steel cursor-pointer";
                  let testTag = `quiz-q${qi}-opt${oi}`;
                  if (locked) {
                    if (oi === correctIdx) {
                      cls = "border-mm-gold bg-mm-gold/15 cursor-default";
                      testTag += "-correct";
                    } else if (isAttempted) {
                      cls = "border-red-300 bg-red-50 cursor-default";
                      testTag += "-wrong";
                    } else {
                      cls = "border-mm-navy/10 opacity-60 cursor-default";
                    }
                  } else if (isFirstWrong) {
                    cls = "border-red-300 bg-red-50 opacity-60 cursor-not-allowed";
                    testTag += "-disabled-wrong";
                  }
                  const clickable = !locked && !isFirstWrong;
                  return (
                    <button
                      type="button"
                      key={`${qi}-${oi}`}
                      disabled={!clickable}
                      onClick={() => clickable && selectOption(qi, oi)}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-colors text-left ${cls}`}
                      data-testid={testTag}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        locked && oi === correctIdx ? "border-mm-gold bg-mm-gold" :
                        locked && isAttempted ? "border-red-400 bg-red-100" :
                        isFirstWrong ? "border-red-300" :
                        "border-mm-navy/30"}`}>
                        {locked && oi === correctIdx && <Check size={12} strokeWidth={3} className="text-mm-navy" />}
                        {locked && isAttempted && oi !== correctIdx && <X size={12} strokeWidth={3} className="text-red-700" />}
                      </div>
                      <span className={`flex-1 ${locked && oi === correctIdx ? "font-bold text-mm-navy" : "text-mm-navy"}`}>{opt}</span>
                    </button>
                  );
                })}
              </div>

              {cur.msg && (
                <div className={`mt-3 text-sm rounded-lg px-3 py-2 ${
                  cur.state === QUESTION_STATE.CORRECT ? "bg-emerald-50 text-emerald-800 border border-emerald-200" :
                  cur.state === QUESTION_STATE.SECOND_WRONG ? "bg-amber-50 text-amber-800 border border-amber-200" :
                  "bg-red-50 text-red-800 border border-red-200"
                }`} data-testid={`quiz-q${qi}-feedback`}>
                  {cur.msg}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-7 flex gap-3">
        <button onClick={onExit} className="btn-trail btn-outline-navy text-sm">Back to guide</button>
        <button
          onClick={submitQuiz}
          disabled={!allAnswered || submitting}
          className="btn-trail btn-gold text-sm disabled:opacity-50"
          data-testid="submit-quiz-btn"
        >
          {submitting ? "Submitting…" : "Plant the flag"}
        </button>
      </div>
    </div>
  );
};

const SummaryStat = ({ label, value, accent }) => (
  <div className={`p-3 rounded-lg ${accent ? "bg-mm-navy text-white" : "bg-white border border-mm-navy/10"}`}>
    <div className={`text-[10px] uppercase tracking-widest font-bold ${accent ? "text-mm-gold" : "text-mm-steel"}`}>{label}</div>
    <div className={`font-heading text-2xl font-black ${accent ? "text-white" : "text-mm-navy"}`}>{value}</div>
  </div>
);

export default QuizFlow;
